import clsx from "clsx";
import prettyBytes from "pretty-bytes";
import { type FC, useRef, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import type { BlockType, WorksheetType } from "../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../shared/worksheets/constants";
import { addAssetToDoc, createBlockOfType } from "../../shared/worksheets/doc-helpers";

import { showOEmbedModal } from "./Field/Embed/EmbedModal";

import { collaborationUploadFile } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { FileUploadField } from "mds/components/FileUploadField";
import { ControlledMenu, MenuDivider, MenuItem } from "mds/components/Menu";
import { useHoverable } from "mds/hooks/use-hoverable";
import {
  ArrowUpFromLineIcon,
  CodeBlockChevronIcon,
  ContentBlockIcon,
  LongFormPencilIcon,
  MultipleChoiceSingleIcon,
  PresentPlayIcon,
  TableLayoutIcon,
  VideoEmbedIcon,
  WhiteboardIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSection, selectIsEditModeEnabled } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { updatePageStaticContentStatus } from "utils/page";

const showEmbedModal = async () => {
  const data = await showOEmbedModal("");

  if (data) {
    const block = createBlockOfType("Embed");
    block.f[0] = { ...block.f[0], ...data };

    return block;
  }
  return null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const BLOCK_TYPE_TO_ICON: Record<BlockType["t"], FC> = {
  Instructions: ContentBlockIcon,
  FreeResponse: LongFormPencilIcon,
  MultipleChoice: MultipleChoiceSingleIcon,
  Table: TableLayoutIcon,
  Embed: PresentPlayIcon,
  Asset: ArrowUpFromLineIcon,
  Code: CodeBlockChevronIcon,
  File: ArrowUpFromLineIcon,
  Whiteboard: WhiteboardIcon,
};

// Note if you modify this list, make sure the menu renders as desired given its formatting depends on the order.
const BLOCK_LIST: {
  type: BlockType["t"];
  labelKey:
    | "content"
    | "free_response"
    | "multiple_choice"
    | "table"
    | "embed"
    | "file_submission"
    | "code_block"
    | "whiteboard";
  Icon: FC;
  customCreationFn?: () => Promise<BlockType | null>;
}[] = [
  { type: "Instructions", labelKey: "content", Icon: ContentBlockIcon },
  { type: "FreeResponse", labelKey: "free_response", Icon: LongFormPencilIcon },
  { type: "MultipleChoice", labelKey: "multiple_choice", Icon: MultipleChoiceSingleIcon },
  { type: "Table", labelKey: "table", Icon: TableLayoutIcon },
  { type: "Embed", labelKey: "embed", Icon: VideoEmbedIcon, customCreationFn: showEmbedModal },
  {
    type: "File",
    labelKey: "file_submission",
    Icon: ArrowUpFromLineIcon,
  },
  { type: "Code", labelKey: "code_block", Icon: CodeBlockChevronIcon },
  { type: "Whiteboard", labelKey: "whiteboard", Icon: WhiteboardIcon },
];

interface AddElementProps {
  doc: Doc<WorksheetType>;
  insertAt: number;
  className?: string;
  isLarge?: boolean;
  pageId: string;
}

export const AddElement: FC<AddElementProps> = ({ doc, insertAt, className, isLarge, pageId }) => {
  const addButtonHover = useHoverable();
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const currentSection = useObjectSelector(selectCurrentSection, ["code_blocks_enabled"]);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isVisible = addButtonHover.isHovering || isOpen || isUploading || doc.data.b.length === 0;

  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "interaction_mode"],
  );
  if (!page) {
    return null;
  }

  const blockListForSection = BLOCK_LIST.filter(
    // Code blocks only if enabled in section
    (block) => (currentSection?.code_blocks_enabled ?? false) || block.type !== "Code",
  );

  const onAddBlock = async (type: BlockType["t"]) => {
    const blockTemplate = BLOCK_LIST.find((block) => block.type === type);
    const customCreationFn = blockTemplate?.customCreationFn;

    const block = customCreationFn ? await customCreationFn() : createBlockOfType(type);

    if (block) {
      doc.submitOp([{ p: [BLOCKS_PATH, insertAt], li: block }]);
      updatePageStaticContentStatus(page, doc);
    }
  };

  const onFileDrop = (files: File[]) => {
    setIsUploading(true);
    const uploadPromises = files.map(collaborationUploadFile);
    uploadPromises.forEach((uploadPromise, i) => {
      // Catch is handled in the Promise.all below

      uploadPromise.then((response) =>
        addAssetToDoc(
          doc,
          {
            url: response.url,
            name: files[i].name,
            size: prettyBytes(files[i].size),
            fileType: files[i].type,
          },
          insertAt,
        ),
      );
    });

    trackEvent("Page - File upload to new content block", BUTTON_EVENT_CATEGORY);
    Promise.all(uploadPromises)
      .catch(toastLocalizedOperationCatcher("upload_failed"))
      .finally(() => setIsUploading(false));
  };

  return (
    <div className={className} {...addButtonHover.hoverParentProps}>
      <FileUploadField
        addButtonProps={{
          type: "block",
          ref,
          disabled: !isEditModeEnabled,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            setIsOpen(true);
          },
        }}
        className={clsx("whitespace-nowrap", !isVisible && "invisible")}
        disabled={!isEditModeEnabled}
        isLoading={isUploading}
        size={isLarge ? "l" : "s"}
        multiple
        onDrop={onFileDrop}
      />
      {/* The reason we use a controlled menu is so that we can keep the button visible
      while the menu is open */}
      <ControlledMenu
        anchorRef={ref as React.RefObject<Element>}
        state={isOpen ? "open" : "closed"}
        onClose={() => setIsOpen(false)}
      >
        {blockListForSection.map(({ type, labelKey, Icon }, index) => (
          <div key={type}>
            {index === 1 && <MenuDivider />}
            <MenuItem
              eventName="Page - Add block"
              eventProps={{ blockType: type }}
              onClick={(e) => {
                e.syntheticEvent.stopPropagation();
                onAddBlock(type);
              }}
            >
              <Icon />
              {t(`add_element.${labelKey}`)}
            </MenuItem>
          </div>
        ))}
      </ControlledMenu>
    </div>
  );
};
